import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const sharepointApiClient = axios.create({
  baseURL: config.api.sharepointServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 10 minutes - increased for large document processing
});

sharepointApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(sharepointApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(sharepointApiClient);


const sharepointService = {
  /**
  * Get list of clients
  * @returns {Promise<Array<string>>}
  */
  /**
   * Each SharePoint client folder's mapped Client Helper client (the client
   * someone last picked for it). -> { [folder]: clientId }
   */
  async getClientFolderMap() {
    try {
      const response = await sharepointApiClient.get('/sharepoint/clients', { params: { withClientIds: true } });
      return Object.fromEntries((response.data || []).filter((f) => f.clientId).map((f) => [f.name, f.clientId]));
    } catch (error) {
      console.warn('Could not load the SharePoint folder clients:', error);
      return {};
    }
  },

  async getClients() {
    try {
      const response = await sharepointApiClient.get('/sharepoint/clients');
      return response.data;
    } catch (error) {
      console.warn('Backend not available, using stub:', error);
      return [
        "Acme Corp",
        "Globex LLC",
        "Wayne Enterprises",
      ];
    }
  },

  /**
   * Upload a file to S3 via the sharepoint service
   * Uses presigned URL for large files (>5MB) to bypass API Gateway limits
   * @param {File} file - The file to upload
   * @param {string} clientName - The client name
   * @param {function} onProgress - Progress callback
   * @returns {Promise<{s3Key: string, url: string, fileId: string}>}
   */
  async uploadFile(file, clientName, clientId, onProgress, { skipAnalysis = false, skipRedaction = false } = {}) {
    const fileSizeMB = file.size / (1024 * 1024);
    const USE_PRESIGNED_THRESHOLD_MB = 3; // Use presigned URL for files larger than 3MB (lowered to avoid API Gateway limits during concurrent uploads)
    
    // For large files, use presigned URL upload
    if (fileSizeMB > USE_PRESIGNED_THRESHOLD_MB) {
      console.log(`File size ${fileSizeMB.toFixed(2)}MB exceeds ${USE_PRESIGNED_THRESHOLD_MB}MB threshold, using presigned URL upload`);
      
      // Step 1: Get presigned URL
      const presignedResponse = await sharepointApiClient.post('/sharepoint/upload/presigned', {
        clientName: clientName,
        filename: file.name,
        clientId: clientId,
        contentType: file.type || 'application/octet-stream',
        skipAnalysis: skipAnalysis,
        skipRedaction: skipRedaction,
      });
      
      const { uploadUrl, fileId, s3Key, fields } = presignedResponse.data;
      
      // Step 2: Upload directly to S3 using presigned URL
      try {
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': fields['Content-Type']
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percent);
            }
          },
        });
        
        // Step 3: Confirm upload completion
        const confirmResponse = await sharepointApiClient.post('/sharepoint/upload/confirm', {
          fileId: fileId,
          clientName: clientName,
          skipAnalysis: skipAnalysis
        });
        
        return {
          success: true,
          fileId: confirmResponse.data.fileId,
          s3Key: confirmResponse.data.s3Key
        };
        
      } catch (uploadError) {
        console.error('Error uploading to S3:', uploadError);
        throw new Error(`Failed to upload file to S3: ${uploadError.message}`);
      }
    } else {
      // For smaller files, use traditional multipart upload through API Gateway
      console.log(`File size ${fileSizeMB.toFixed(2)}MB, using direct upload`);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientName', clientName);
      formData.append('clientId', clientId || '');
      if (skipAnalysis) formData.append('skipAnalysis', 'true');
      if (skipRedaction) formData.append('skipRedaction', 'true');

      const response = await sharepointApiClient.post('/sharepoint/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      });

      return response.data;
    }
  },

  /**
   * Upload to S3 (alias for uploadFile for backward compatibility)
   * @param {File} file - The file to upload
   * @param {string} clientName - The client name
   * @param {function} onProgress - Progress callback
   * @returns {Promise<{s3Key: string, url: string}>}
   */
  async uploadToS3(file, clientName, onProgress) {
    return this.uploadFile(file, clientName, onProgress);
  },

  downloadFile: async (filename, client_id, convertToPdf = false, s3Key = null) => {
    try {
      console.log('Downloading file:', { filename, client_id, convertToPdf });

      let resolvedClientId = client_id;
      if (s3Key) {
        const keyPrefix = s3Key.split('/')[0];
        if (keyPrefix) {
          resolvedClientId = keyPrefix;
        }
      }

      const params = {
        filename: filename,
        client_id: resolvedClientId
      };

      if (s3Key) {
        params.s3_key = s3Key;
      }

      // Add convert_to_pdf parameter if needed
      if (convertToPdf) {
        params.convert_to_pdf = true;
      }

      // Get presigned URL from backend
      const response = await sharepointApiClient.get(`sharepoint/get-file`, {
        params: params
      });

      console.log('Response received:', response.status, response.data);

      const downloadUrl = response.data.download_url;
      if (!downloadUrl) {
        throw new Error('No download URL returned from server');
      }

      // Fetch the file directly from the presigned URL
      const fileResponse = await fetch(downloadUrl);
      
      if (!fileResponse.ok) {
        throw new Error(`Failed to download file: ${fileResponse.status}`);
      }

      const blob = await fileResponse.blob();
      console.log('Downloaded blob:', blob.size, blob.type);

      // Check if blob has content
      if (blob.size === 0) {
        throw new Error(`File not found or empty: ${filename}`);
      }

      // Ensure proper content type for PDF files
      if (filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        console.log('Created PDF blob:', pdfBlob.size, pdfBlob.type);
        return { blob: pdfBlob, headers: {} };
      }

      return { blob, headers: {} };
    }
    catch (error) {
      console.error('Error downloading file:', error);
      console.error('Error response:', error.response?.data, error.response?.status, error.response?.headers);
      throw error;
    }
  },

  /**
   * Rename a file (placeholder - backend doesn't have this endpoint yet)
   * @param {string} s3Key - The S3 key of the file
   * @returns {Promise<{newName: string}>}
   */
  async renameFile(s3Key) {
    // For now, just return the original name as the new name
    // This can be updated when the backend rename endpoint is implemented
    const originalName = s3Key.split('/').pop().split('_').slice(1).join('_') || s3Key;
    return { newName: originalName };
  },

  /**
   * List uploads for a client
   * @param {string} clientName - The client name
   * @returns {Promise<{files: Array}>}
   */
  async listUploads(clientName) {
    const response = await sharepointApiClient.get('/sharepoint/uploads', {
      params: { clientName }
    });
    return response.data.files || [];
  },

  /**
   * Get SharePoint-upload redaction settings.
   * @returns {Promise<{enabled: boolean, categories: string[], availableCategories: string[]}>}
   */
  async getRedactionSettings() {
    const response = await sharepointApiClient.get('/sharepoint/redaction-settings');
    return response.data;
  },

  /**
   * Update SharePoint-upload redaction settings.
   * @param {boolean} enabled
   * @param {string[]} categories
   * @returns {Promise<{enabled: boolean, categories: string[], availableCategories: string[]}>}
   */
  async updateRedactionSettings(enabled, categories) {
    const response = await sharepointApiClient.put('/sharepoint/redaction-settings', {
      enabled,
      categories,
    });
    return response.data;
  },

  /**
   * Push file to SharePoint (placeholder)
   * @param {string} s3Key - The S3 key
   * @param {string} fileName - The file name
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean, location?: string}>}
   */
  async pushToSharepoint(s3Key, fileName, clientName) {
    // Placeholder - implement when backend endpoint is ready
    console.log(`Pushing ${s3Key} as ${fileName} for ${clientName} to SharePoint`);
    return { success: true, location: `sharepoint:///${clientName}/${fileName}` };
  },

  /**
   * Delete file from S3, SharePoint storage, and DynamoDB metadata table
   * @param {string} fileId - The file ID to delete
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean, message?: string, details?: object}>}
   */
  async deleteFile(fileId, clientName) {
    if (!fileId || !clientName) {
      throw new Error('fileId and clientName are required for file deletion');
    }

    try {
      console.log(`Deleting file ${fileId} for client ${clientName}`);

      const response = await sharepointApiClient.delete('/sharepoint/delete', {
        data: {
          fileId: fileId,
          clientName: clientName
        }
      });

      console.log('Delete response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);

      // Re-throw with a more descriptive message
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || 'Unknown error occurred';
      throw new Error(`Failed to delete file: ${errorMessage}`);
    }
  },

  /**
   * Delete file from S3 (backward compatibility - now calls comprehensive delete)
   * @param {string} s3Key - The S3 key to delete (will be used to find fileId)
   * @returns {Promise}
   */
  async deleteFromS3(s3Key) {
    // For backward compatibility, we need to find the file by s3Key
    // This is a legacy method - should use deleteFile directly with fileId
    console.warn('deleteFromS3 is deprecated, use deleteFile with fileId instead');
    console.log(`Legacy delete request for S3 key: ${s3Key}`);

    // For now, we'll throw an error suggesting to use the new method
    throw new Error('deleteFromS3 is deprecated. Please use deleteFile(fileId, clientName) instead.');
  },

  /**
   * Analyze files by their file IDs (async workflow - returns job IDs immediately)
   * @param {Array<string>} fileIds - Array of file IDs
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean, results?: Array<{fileId: string, jobId: string}>}>}
   * Each result contains fileId and jobId for polling
   */
  async analyzeFilesByIds(fileIds, clientName) {
    const response = await sharepointApiClient.post('/sharepoint/analyze', {
      clientName: clientName,
      fileIds: fileIds
    });
    return response.data;
  },

  /**
   * Get workflow status for a file analysis job
   * @param {string} jobId - The workflow job ID
   * @returns {Promise<{status: string, file_id?: string, client_name?: string, suggested_filename?: string, category?: string, summary?: string, error?: string, filename_llm_details?: object, summary_llm_details?: object}>}
   * Status values: pending, analyzing_filename, filename_generated, determining_category, summarizing, completed, failed
   */
  async getWorkflowStatus(jobId) {
    const response = await sharepointApiClient.get(`/sharepoint/workflow/${jobId}`);
    return response.data;
  },

  /**
   * Update the suggested file name for a file
   * @param {string} fileId - The file ID
   * @param {string} newName - The new suggested name
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean}>}
   */
  async updateFileName(fileId, newName, clientName) {
    const response = await sharepointApiClient.put('/sharepoint/update-filename', {
      fileId: fileId,
      newName: newName,
      clientName: clientName
    });
    return response.data;
  },

  /**
   * Get document summary for a file (initiates async job)
   * @param {string} fileId - The document key (s3Key or fileId)
   * @param {string} clientName - The client name
   * @param {string} customSystemPrompt - Custom system prompt for summary generation (optional)
   * @param {string} customUserPrompt - Custom user prompt for summary generation (optional)
   * @param {boolean} useTextract - Whether to use Textract OCR for PDF text extraction (optional)
   * @param {string} modelId - Bedrock model ID from the selected custom prompt (optional)
   * @returns {Promise<{success: boolean, jobId?: string, error?: string}>}
   */
  async getDocumentSummary(fileId, clientName, customSystemPrompt, customUserPrompt, useTextract, modelId) {
    const requestBody = {
      fileIds: [fileId],
      clientName: clientName
    };

    // Add custom prompts if provided
    if (customSystemPrompt) {
      requestBody.customSystemPrompt = customSystemPrompt;
    }
    if (customUserPrompt) {
      requestBody.customUserPrompt = customUserPrompt;
    }

    if (modelId) {
      requestBody.modelId = modelId;
    }
    
    // Add useTextract flag if provided
    if (useTextract !== undefined && useTextract !== null) {
      requestBody.useTextract = useTextract;
    }

    const response = await sharepointApiClient.post('/sharepoint/documents/summary', requestBody);
    
    // Extract job_id from response
    if (response.data.success && response.data.results && response.data.results.length > 0) {
      const jobId = response.data.results[0].jobId; // job_id is in jobId field
      return { success: true, jobId };
    }
    
    return { 
      success: false, 
      error: response.data.error || 'Failed to create summary job' 
    };
  },

  /**
   * Get document summary job status
   * @param {string} jobId - The job identifier
   * @returns {Promise<{status: string, summary?: string, error?: string}>}
   */
  async getDocumentSummaryJobStatus(jobId) {
    const response = await sharepointApiClient.get(`/sharepoint/documents/summary/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Get available folders for a client
   * @param {string} clientName - The client name
   * @param {string} basePath - Optional known base path to skip search
   * @returns {Promise<{folders: Array}>}
   */
  async getClientFolders(clientName, basePath = null) {
    let url = `/sharepoint/client/${encodeURIComponent(clientName)}/folders`;
    if (basePath) {
      url += `?basePath=${encodeURIComponent(basePath)}`;
    }
    const response = await sharepointApiClient.get(url);
    return response.data;
  },

  /**
   * Push file to SharePoint and optionally to knowledgebase
   * @param {string} fileId - The file ID
   * @param {string} clientName - The client name
   * @param {string} folderId - The target folder ID
   * @param {string} folderPath - The full SharePoint folder path
   * @param {string} knowledgebaseClientId - Optional knowledgebase client ID
   * @param {boolean} overwrite - Whether to overwrite existing files
   * @returns {Promise<{success: boolean, message?: string, sharepoint_location?: string}>}
   */
  async pushToSharePointAndKnowledgebase(fileId, clientName, knowledgebaseClientId, folderId, folderPath, overwrite = false, docType = '', subType = '', accountNumber = '', uploadOriginal = false) {
    const requestBody = {
      fileId: fileId,
      clientName: clientName,
      knowledgebaseClientId: knowledgebaseClientId,
      folderId: folderId,
      folderPath: folderPath,
      overwrite: overwrite,
      uploadOriginal,
      docType: docType || 'Mixed',
      subType: subType || 'Mixed',
      accountNumber: accountNumber || 'NONE'
    };

    const response = await sharepointApiClient.post('/sharepoint/push', requestBody);
    return response.data;
  },

  /**
   * Create a new folder in SharePoint
   * @param {string} folderName - Name of the new folder
   * @param {string} parentFolderPath - Path to the parent folder
   * @param {string} clientName - The client name
   * @param {boolean} returnExisting - If true, return existing folder info instead of error
   * @returns {Promise<{success: boolean, folder?: object, message?: string, error?: string, already_existed?: boolean}>}
   */
  async createFolder(folderName, parentFolderPath, clientName, returnExisting = false) {
    const requestBody = {
      folderName: folderName,
      parentFolderPath: parentFolderPath,
      clientName: clientName,
      returnExisting: returnExisting
    };

    const response = await sharepointApiClient.post('/sharepoint/create-folder', requestBody);
    return response.data;
  },

  /**
   * Create a new client in SharePoint
   * @param {string} clientName - Name of the new client
   * @returns {Promise<{success: boolean, client?: object, message?: string, error?: string}>}
   */
  async createClient(clientName) {
    const requestBody = {
      clientName: clientName
    };

    const response = await sharepointApiClient.post('/sharepoint/create-client', requestBody);
    return response.data;
  },

  /**
   * Save metadata for a file
   * @param {string} fileId - The file ID
   * @param {string} clientName - The client name
   * @param {string} editedSummary - Edited document summary (optional)
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  async saveFileMetadata(fileId, clientName, editedSummary, docType, subType, accountNumber) {
    try {
      const requestBody = {
        fileId: fileId,
        clientName: clientName
      };

      // Add edited summary if provided
      if (editedSummary !== undefined && editedSummary !== null) {
        requestBody.editedSummary = editedSummary;
      }

      // Add doc_type, sub_type, account_number if provided
      if (docType !== undefined) {
        requestBody.docType = docType;
      }
      if (subType !== undefined) {
        requestBody.subType = subType;
      }
      if (accountNumber !== undefined) {
        requestBody.accountNumber = accountNumber;
      }

      const response = await sharepointApiClient.put('/sharepoint/update-metadata', requestBody);
      return response.data;
    } catch (error) {
      console.error('Error saving file metadata:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Approve filename for a file
   * @param {string} fileId - The file ID
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  async approveFilename(fileId, clientName) {
    try {
      const requestBody = {
        fileId: fileId,
        clientName: clientName
      };

      const response = await sharepointApiClient.post('/sharepoint/approve-filename', requestBody);
      return response.data;
    } catch (error) {
      console.error('Error approving filename:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Approve summary for a file (marks file as done)
   * @param {string} fileId - The file ID
   * @param {string} clientName - The client name
   * @returns {Promise<{success: boolean, message?: string, error?: string}>}
   */
  async approveSummary(fileId, clientName) {
    try {
      const requestBody = {
        fileId: fileId,
        clientName: clientName
      };

      const response = await sharepointApiClient.post('/sharepoint/approve-summary', requestBody);
      return response.data;
    } catch (error) {
      console.error('Error approving summary:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Approve both filename and summary in a single operation.
   * This pushes the file to SharePoint and knowledgebase, then marks it as done.
   * 
   * @param {string} fileId - The file ID
   * @param {string} clientName - The client name
   * @param {string} folderId - The target folder ID
   * @param {string} folderPath - The full SharePoint folder path
   * @param {string} knowledgebaseClientId - Knowledgebase client ID
   * @param {boolean} overwrite - Whether to overwrite existing files
   * @returns {Promise<{success: boolean, message?: string, sharepoint_location?: string, sharepoint_url?: string, knowledgebase_status?: string, error?: string}>}
   */
  async approveFileAndSummary(fileId, clientName, knowledgebaseClientId, folderId, folderPath, overwrite = false, docType = '', subType = '', accountNumber = '', uploadOriginal = false) {
    try {
      const requestBody = {
        fileId: fileId,
        clientName: clientName,
        folderId: folderId,
        folderPath: folderPath,
        knowledgebaseClientId: knowledgebaseClientId,
        overwrite: overwrite || false,
        uploadOriginal,
        docType: docType || 'Mixed',
        subType: subType || 'Mixed',
        accountNumber: accountNumber || 'NONE'
      };

      const response = await sharepointApiClient.post('/sharepoint/approve-file-and-summary', requestBody);
      return response.data;
    } catch (error) {
      console.error('Error approving file and summary:', error);
      const detail = error?.response?.data?.detail;
      const message = error?.response?.data?.message;
      return {
        success: false,
        error: detail || message || error.message,
        message: detail || message || error.message,
        statusCode: error?.response?.status,
      };
    }
  },

  /**
   * Generate and download a PDF from document summary
   * @param {string} fileId - The file ID
   * @param {string} summary - The summary content (HTML)
   * @param {string} clientName - The client name
   * @returns {Promise<Blob>} - Returns a Blob containing the PDF
   */
  async exportSummaryToPdf(fileId, summary, clientName) {
    try {
      const requestBody = {
        fileId: fileId,
        summary: summary,
        clientName: clientName
      };

      // Use arraybuffer responseType to properly handle binary PDF data
      const response = await sharepointApiClient.post('/sharepoint/export-summary-pdf', requestBody, {
        responseType: 'arraybuffer'
      });
      
      // Convert arraybuffer to blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      return blob;
    } catch (error) {
      console.error('Error exporting summary to PDF:', error);
      throw error;
    }
  },

  /**
   * Get recent upload history
   * @param {boolean} excludeDone - Whether to exclude items with 'done' status
   * @returns {Promise<{records: Array, count: number}>}
   */
  async getRecentHistory(excludeDone = false) {
    try {
      const response = await sharepointApiClient.get('/sharepoint/recent-history', {
        params: {
          exclude_done: excludeDone
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent history:', error);
      throw error;
    }
  },

  // =============================================================================
  // Backfill Methods - Reorganize existing SharePoint files
  // =============================================================================

  /**
   * Get predefined folder options for backfill organization
   * @returns {Promise<{folders: Array<{id: string, name: string, description: string}>}>}
   */
  async getBackfillFolderOptions() {
    try {
      const response = await sharepointApiClient.get('/sharepoint/backfill/folders');
      return response.data;
    } catch (error) {
      console.error('Error fetching backfill folder options:', error);
      throw error;
    }
  },

  /**
   * Scan a client's SharePoint folder for existing files
   * @param {string} clientName - The client name to scan
   * @param {string} [knowledgebaseClientId] - Optional knowledgebase client ID to check which files are already in KB
   * @returns {Promise<{success: boolean, clientName: string, files: Array, totalCount: number}>}
   */
  async scanClientFiles(clientName, knowledgebaseClientId = null) {
    try {
      const payload = {
        clientName: clientName
      };
      if (knowledgebaseClientId) {
        payload.knowledgebaseClientId = knowledgebaseClientId;
      }
      const response = await sharepointApiClient.post('/sharepoint/backfill/scan', payload);
      return response.data;
    } catch (error) {
      console.error('Error scanning client files:', error);
      throw error;
    }
  },

  /**
   * Analyze selected files for backfill suggestions
   * @param {string} clientName - The client name
   * @param {Array<string>} fileIds - SharePoint item IDs to analyze
   * @returns {Promise<{success: boolean, results: Array<{fileId: string, jobId: string}>}>}
   */
  async analyzeBackfillFiles(clientName, fileIds) {
    try {
      const response = await sharepointApiClient.post('/sharepoint/backfill/analyze', {
        clientName: clientName,
        fileIds: fileIds
      });
      return response.data;
    } catch (error) {
      console.error('Error analyzing backfill files:', error);
      throw error;
    }
  },

  /**
   * Get the status of a backfill analysis job
   * @param {string} jobId - The job ID
   * @returns {Promise<{job_id: string, status: string, suggested_name: string, suggested_folder: string, ...}>}
   */
  async getBackfillJobStatus(jobId) {
    try {
      const response = await sharepointApiClient.get(`/sharepoint/backfill/status/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting backfill job status:', error);
      throw error;
    }
  },

  /**
   * Apply backfill changes - rename, move, and add to knowledgebase
   * @param {Object} params - Apply parameters
   * @param {string} params.clientName - Client name
   * @param {string} params.fileId - SharePoint item ID
   * @param {string} params.currentPath - Current file path in SharePoint
   * @param {string} params.newName - New filename
   * @param {string} params.targetFolder - Target folder name
   * @param {boolean} params.addToKnowledgebase - Whether to add to knowledgebase
   * @param {string} params.knowledgebaseClientId - Knowledgebase client ID
   * @param {string} [params.existingKbFileId] - Existing KB file ID to delete/replace
   * @returns {Promise<{success: boolean, newPath: string, sharepointUrl: string, knowledgebaseStatus: string}>}
   */
  async applyBackfillChanges(params) {
    try {
      const payload = {
        clientName: params.clientName,
        fileId: params.fileId,
        currentPath: params.currentPath,
        newName: params.newName,
        targetFolder: params.targetFolder,
        addToKnowledgebase: params.addToKnowledgebase || false,
        knowledgebaseClientId: params.knowledgebaseClientId,
        docTypeId: params.docTypeId,
        subTypeId: params.subTypeId,
        accountNumber: params.accountNumber
      };
      // Include existingKbFileId if the file is already in KB (for delete/replace)
      if (params.existingKbFileId) {
        payload.existingKbFileId = params.existingKbFileId;
      }
      // Include originalS3Key if available (from backfill analysis staging)
      // This allows the backend to use the existing S3 file instead of re-downloading from SharePoint
      if (params.originalS3Key) {
        payload.originalS3Key = params.originalS3Key;
      }
      const response = await sharepointApiClient.post('/sharepoint/backfill/apply', payload);
      return response.data;
    } catch (error) {
      console.error('Error applying backfill changes:', error);
      throw error;
    }
  },

  /**
   * Relocate (rename/move) a file that's already in SharePoint
   * This allows changing the filename and/or folder of an already-approved file.
   * It will also update the knowledgebase with the new file location.
   * @param {Object} params - Relocate parameters
   * @param {string} params.fileId - DynamoDB file ID for metadata lookup
   * @param {string} params.clientName - Client name
   * @param {string} params.currentPath - Current full path in SharePoint (e.g., "folder/subfolder/file.pdf")
   * @param {string} params.newName - New filename
   * @param {string} params.targetFolder - Target folder path
   * @param {string} [params.knowledgebaseClientId] - Knowledgebase client ID
   * @returns {Promise<{success: boolean, newPath: string, sharepointUrl: string, knowledgebaseStatus: string, message: string}>}
   */
  async relocateFile(params) {
    try {
      const payload = {
        fileId: params.fileId,
        clientName: params.clientName,
        currentPath: params.currentPath,
        newName: params.newName,
        targetFolder: params.targetFolder,
        knowledgebaseClientId: params.knowledgebaseClientId
      };
      const response = await sharepointApiClient.post('/sharepoint/relocate', payload);
      return response.data;
    } catch (error) {
      console.error('Error relocating file:', error);
      throw error;
    }
  },

  /**
   * Delete a file during backfill process
   * @param {string} clientName - Client name
   * @param {string} fileId - SharePoint item ID
   * @param {string} filePath - File path in SharePoint
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteBackfillFile(clientName, fileId, filePath) {
    try {
      const response = await sharepointApiClient.post('/sharepoint/backfill/delete', {
        clientName: clientName,
        fileId: fileId,
        filePath: filePath
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting backfill file:', error);
      throw error;
    }
  },

  // ========================================
  // State Machine Backfill Endpoints
  // ========================================

  /**
   * Start a backfill analysis job using state machine
   * @param {string} clientName - The client name
   * @param {Array} files - Array of file objects to analyze
   * @returns {Promise<{jobId: string, status: string, message: string}>}
   */
  async startBackfillJob(clientName, files) {
    try {
      const response = await sharepointApiClient.post('/sharepoint/backfill/job/start', {
        clientName: clientName,
        files: files
      });
      return response.data;
    } catch (error) {
      console.error('Error starting backfill job:', error);
      throw error;
    }
  },

  /**
   * Get the status of a backfill job from state machine
   * @param {string} jobId - The job ID returned from startBackfillJob
   * @returns {Promise<{jobId: string, clientName: string, status: string, totalFiles: number, processedFiles: number, results: Array, createdAt: string, updatedAt: string}>}
   */
  async getBackfillJobStatus(jobId) {
    try {
      const response = await sharepointApiClient.get(`/sharepoint/backfill/job/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting backfill job status:', error);
      throw error;
    }
  },

  /**
   * List backfill jobs, optionally filtered
   * @param {string} clientName - Optional client name filter
   * @param {string} status - Optional status filter (pending, processing, completed, failed)
   * @returns {Promise<{jobs: Array}>}
   */
  async listBackfillJobs(clientName = null, status = null) {
    try {
      const params = new URLSearchParams();
      if (clientName) params.append('client_name', clientName);
      if (status) params.append('status_filter', status);
      
      const url = `/sharepoint/backfill/jobs${params.toString() ? '?' + params.toString() : ''}`;
      const response = await sharepointApiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error listing backfill jobs:', error);
      throw error;
    }
  },

  /**
   * Cancel and delete a backfill job
   * This will stop the state machine execution if running and remove the job from DynamoDB
   * @param {string} jobId - The job ID to cancel/delete
   * @returns {Promise<{success: boolean, jobId: string, executionStopped: boolean, message: string}>}
   */
  async cancelBackfillJob(jobId) {
    try {
      const response = await sharepointApiClient.delete(`/sharepoint/backfill/job/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling backfill job:', error);
      throw error;
    }
  },

  /**
   * Mark a backfill job as completed after applying changes
   * @param {string} jobId - The job ID to mark as completed
   * @returns {Promise<{success: boolean, jobId: string, status: string, message: string}>}
   */
  async completeBackfillJob(jobId) {
    try {
      const response = await sharepointApiClient.post(`/sharepoint/backfill/job/${jobId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Error completing backfill job:', error);
      throw error;
    }
  },

  /**
   * Update the status of a single file in a backfill job
   * @param {string} jobId - The job ID
   * @param {string} fileId - The file ID to update
   * @param {string} status - New status: 'applied', 'skipped', 'deleted', 'failed'
   * @param {string} error - Optional error message if status is 'failed'
   * @returns {Promise<{success: boolean, jobId: string, fileId: string, fileStatus: string, jobStatus: string, allFilesProcessed: boolean, message: string}>}
   */
  async updateBackfillFileStatus(jobId, fileId, status, error = null) {
    try {
      const payload = { status };
      if (error) payload.error = error;
      const response = await sharepointApiClient.post(
        `/sharepoint/backfill/job/${jobId}/file/${fileId}/status`,
        payload
      );
      return response.data;
    } catch (err) {
      console.error('Error updating backfill file status:', err);
      throw err;
    }
  },

  /**
   * Persist user-edited review decisions for a backfill job to DynamoDB.
   * Called with a debounce as the user edits filenames, folders, etc.
   *
   * @param {string} jobId - The backfill job ID
   * @param {Object} decisions - fileId -> { action, newName, targetFolder, docTypeId, subTypeId, accountNumber }
   * @returns {Promise<{success: boolean, updated: number}>}
   */
  async saveBackfillDecisions(jobId, decisions) {
    try {
      const response = await sharepointApiClient.post(
        `/sharepoint/backfill/job/${jobId}/decisions`,
        { decisions }
      );
      return response.data;
    } catch (err) {
      console.error('Error saving backfill decisions:', err);
      throw err;
    }
  },

};

export default sharepointService;
